import * as React from 'react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { pdf } from '@react-pdf/renderer';
import Alert from '@cloudscape-design/components/alert';
import Box from '@cloudscape-design/components/box';
import Button from '@cloudscape-design/components/button';
import ContentLayout from '@cloudscape-design/components/content-layout';
import FormField from '@cloudscape-design/components/form-field';
import Header from '@cloudscape-design/components/header';
import Select from '@cloudscape-design/components/select';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Table from '@cloudscape-design/components/table';
import { reportNonFatal, serializeError } from '@/lib/errorReport';
import { trackEvents } from '@/lib/events';
import { toast } from '@/lib/notifications';
import { MonthlyReportPdf } from './MonthlyReportPdf';
import { useMonthlyReport } from './useMonthlyReport';
import { serverDate } from '@etiquetachef/ui-shared/online';

const MONTHS_BACK = 12;

/** Os 12 meses do ano (`yyyymm`), de dezembro para janeiro. */
function buildMonthList(year: number): string[] {
  const out: string[] = [];
  for (let m = 12; m >= 1; m--) {
    out.push(`${String(year)}${String(m).padStart(2, '0')}`);
  }
  return out;
}

/** `yyyymm` → `MM/AAAA` para exibição. */
function formatMonth(yyyymm: string): string {
  return `${yyyymm.slice(4, 6)}/${yyyymm.slice(0, 4)}`;
}

/**
 * Tela `/reports`: escolher um mês e baixar o relatório mensal de BI em PDF.
 *
 * Origem: sem issue — commit fff721034, 2026-05-04 18:55 +0200 (rota /reports + PDF mensal).
 *
 * Contrato: lista os 12 meses do ano escolhido (ano corrente e 12 anteriores,
 * pelo relógio do navegador — só UI). Escolher um mês monta
 * `ExportSelectedMonth`, que carrega, gera e baixa o PDF; nada é gravado.
 *
 * Usado por: `routes/router.tsx` (rota `reports`, sob `RequireSuperAdmin`).
 *
 * Arquitetura: PDF gerado no cliente (`@react-pdf/renderer`) a partir dos
 * agregados do BI (`useMonthlyReport`); não há callable de relatório.
 */
export function ReportsPage(): React.JSX.Element {
  const { t } = useTranslation();
  const now = useMemo(() => serverDate(), []);
  const [year, setYear] = useState<number>(now.getUTCFullYear());
  const [selected, setSelected] = useState<string | null>(null);
  // #1021 (2026-10-25 15:00 +0100): cada clique em "Exportar PDF" é um pedido novo. Sem o contador,
  // clicar de novo no MESMO mês depois de uma falha não mudava `selected` e não fazia nada; e a troca
  // de mês reaproveitava o `ExportSelectedMonth` montado, com o erro do mês anterior na tela.
  const [requestNonce, setRequestNonce] = useState(0);

  const months = useMemo(() => buildMonthList(year), [year]);
  const yearOptions: { value: string; label: string }[] = [];
  for (let y = now.getUTCFullYear(); y >= now.getUTCFullYear() - MONTHS_BACK; y--) {
    yearOptions.push({ value: String(y), label: String(y) });
  }

  return (
    <ContentLayout
      header={<Header variant="h1" description={t('reports.subtitle')}>{t('reports.title')}</Header>}
    >
      <SpaceBetween size="l">
        <FormField label={t('reports.year')}>
          <Select
            selectedOption={yearOptions.find((o) => o.value === String(year)) ?? null}
            options={yearOptions}
            onChange={({ detail }): void => {
              setYear(Number(detail.selectedOption.value));
            }}
          />
        </FormField>

        <Table<string>
          items={months}
          trackBy={(m): string => m}
          variant="container"
          header={<Header variant="h2">{t('reports.list.title')}</Header>}
          columnDefinitions={[
            {
              id: 'month',
              header: t('reports.list.month'),
              cell: (yyyymm): React.ReactNode => formatMonth(yyyymm),
            },
            {
              id: 'actions',
              header: t('reports.list.actions'),
              cell: (yyyymm): React.ReactNode => (
                <Button
                  iconName="download"
                  onClick={(): void => {
                    setSelected(yyyymm);
                    setRequestNonce((n) => n + 1);
                  }}
                >
                  {t('reports.list.exportPdf')}
                </Button>
              ),
            },
          ]}
        />

        {selected && (
          <ExportSelectedMonth
            // `key` por pedido: remonta, zera o erro do pedido anterior e refaz a carga (#1021).
            key={`${selected}-${String(requestNonce)}`}
            yyyymm={selected}
            onDone={(): void => {
              setSelected(null);
            }}
          />
        )}
      </SpaceBetween>
    </ContentLayout>
  );
}

/**
 * Gera e baixa o PDF de `yyyymm` assim que os dados ficam `READY`; chama
 * `onDone` ao terminar e emite `bi_pdf_exported` (`trackEvents.biPdfExported`).
 *
 * Origem: sem issue — commit fff721034, 2026-05-04 18:55 +0200 (junto da tela).
 *
 * Contrato: erro de geração do PDF → `reportNonFatal('admin_reports_pdf_failed')`
 * com a mensagem crua do react-pdf (só no report), e `reports.pdfFailed`
 * traduzido em `Alert` inline e em toast. Falha de carga dos dados (`ERROR` do
 * `useMonthlyReport`, que já reporta) → `reports.loadFailed` em `Alert` e em
 * toast, uma vez. Montado com `key` por pedido: cada clique começa limpo.
 *
 * Usado por: `ReportsPage` (este arquivo).
 *
 * Histórico:
 *  - 2026-10-25 14:29 +0100 — (#1021) toast + report da falha do PDF.
 *  - 2026-10-25 15:00 +0100 — (#1021) mensagem traduzida (a do react-pdf só no
 *    report), toast na falha de carga e erro zerado a cada pedido.
 */
function ExportSelectedMonth({
  yyyymm,
  onDone,
}: {
  yyyymm: string;
  onDone: () => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  const state = useMonthlyReport(yyyymm);
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (state.kind !== 'READY') return;
    const guard = { cancelled: false };
    /** Renderiza o PDF em blob e dispara o download por âncora temporária. */
    async function generate(): Promise<void> {
      if (state.kind !== 'READY') return;
      setBusy(true);
      try {
        const blob = await pdf(
          <MonthlyReportPdf
            yyyymm={state.report.yyyymm}
            mrrEndUsdCents={state.report.mrrEndUsdCents}
            netNewMrrUsdCents={state.report.netNewMrrUsdCents}
            newTenants={state.report.newTenants}
            churnedTenants={state.report.churnedTenants}
            churnRatePct={state.report.churnRatePct}
            series={state.report.series}
            cohort={state.report.cohort}
            highAlerts={state.report.highAlerts}
          />,
        ).toBlob();
        if (guard.cancelled) return;
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `etiquetachef-relatorio-${yyyymm}.pdf`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        trackEvents.biPdfExported(yyyymm);
        onDone();
      } catch (err) {
        if (guard.cancelled) return;
        // Auditoria 2026-10-25 14:29 +0100 (#1021): o erro ficava só abaixo da tabela de 12 meses, longe do
        // "Exportar PDF" da linha clicada, e não ia para a telemetria. Agora toast (Flashbar fixo) + report.
        // Auditoria 2026-10-25 15:00 +0100 (#1021): a tela mostrava o `err.message` cru do react-pdf
        // (inglês, interno). Ele fica só no report; o usuário vê a mensagem traduzida.
        reportNonFatal('admin_reports_pdf_failed', { yyyymm, ...serializeError(err) });
        const message = t('reports.pdfFailed');
        setError(message);
        toast.error(message);
      } finally {
        if (!guard.cancelled) setBusy(false);
      }
    }
    void generate();
    return (): void => {
      guard.cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.kind]);

  // Auditoria 2026-10-25 15:00 +0100 (#1021): a falha de carga dos dados só aparecia no Alert abaixo
  // da tabela de 12 meses, longe do botão clicado. Toast uma vez por pedido (o `useMonthlyReport` já
  // reporta a causa; a `key` do pai remonta este componente a cada clique).
  React.useEffect(() => {
    if (state.kind === 'ERROR') toast.error(t('reports.loadFailed'));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- só na transição para ERROR
  }, [state.kind]);

  return (
    <Box fontSize="body-s" color="text-status-inactive">
      {state.kind === 'LOADING' && t('common.loading')}
      {state.kind === 'ERROR' && <Alert type="error">{t('reports.loadFailed')}</Alert>}
      {state.kind === 'READY' && busy && t('reports.generating')}
      {error && <Alert type="error">{error}</Alert>}
    </Box>
  );
}
